// File: src/pages/business/CandidateDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import UpgradeModal from '../../components/UpgradeModal'; // ✅ Import Modal Nâng cấp

export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- STATE ---
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [testDetails, setTestDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('result');
  const [matchAnalysis, setMatchAnalysis] = useState(null);
  const [analyzingMatch, setAnalyzingMatch] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ show: false, message: '' });

  const [aiEvaluation, setAiEvaluation] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const [rejectEmail, setRejectEmail] = useState({ subject: '', content: '' });
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, title: '', message: '' });

  const [interviewForm, setInterviewForm] = useState({
    datetime: '',
    type: 'Online',
    location: '',
    note: ''
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // --- HELPER: SHOW TOAST ---
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // --- 1. FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('skillmatch_token');
        if (!token) {
          // Nếu không có token -> Đá về login ngay
          navigate('/login');
          return;
        }

        // ✅ FIX LỖI CÚ PHÁP Ở ĐÂY:
        // Cũ (Sai): await fetch(...), { ... });
        // Mới (Đúng): await fetch(..., { ... });
        const candRes = await fetch(`${API_URL}/api/candidates/${id}`, {
          headers: { 'x-auth-token': token }
        });

        // ✅ CHECK LỖI 403 (HẾT HẠN GÓI)
        if (candRes.status === 403) {
          const data = await candRes.json();
          if (data.requireUpgrade) {
            setUpgradeModal({ show: true, message: data.message });
            return; // Dừng, không setCandidate
          }
        }

        // ✅ CHECK LỖI 401 (TOKEN HẾT HẠN/SAI)
        if (candRes.status === 401) {
          localStorage.removeItem('skillmatch_token');
          navigate('/login');
          return;
        }

        if (!candRes.ok) {
          const errData = await candRes.json();
          throw new Error(errData.message || 'Không tìm thấy ứng viên');
        }

        const candData = await candRes.json();
        setCandidate(candData);
        if (candData.matchAnalysis) setMatchAnalysis(candData.matchAnalysis);
        if (candData.aiEvaluation && candData.aiEvaluation.status === 'Completed') setAiEvaluation(candData.aiEvaluation);

        if (candData.testId) {
          const testRes = await fetch(`${API_URL}/api/tests/${candData.testId}`, { headers: { 'x-auth-token': token } });
          if (testRes.ok) setTestDetails(await testRes.json());
        }
      } catch (error) {
        console.error("Lỗi fetch:", error);
        // showToast("Lỗi tải dữ liệu: " + error.message, 'error'); // Tạm tắt toast lỗi load để tránh spam
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id, API_URL, navigate]);

  const handleGenerateRejectEmail = async () => {
    setIsGeneratingEmail(true);
    try {
      const token = localStorage.getItem('skillmatch_token');
      const res = await fetch(`${API_URL}/api/candidates/${id}/generate-rejection`, {
        method: 'POST', headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      setRejectEmail({ subject: data.subject, content: data.content });
    } catch (e) {
      showToast("Lỗi tạo email", "error");
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const handleGenerateAI = async () => {
    setIsGeneratingAI(true);
    try {
      const token = localStorage.getItem('skillmatch_token');
      const res = await fetch(`${API_URL}/api/candidates/${id}/evaluate`, {
        method: 'POST',
        headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      setAiEvaluation(data);
      setCandidate(prev => ({ ...prev, aiEvaluation: data }));
      showToast("Đã hoàn tất phân tích AI!", "success");
    } catch (err) {
      console.error(err);
      showToast("Lỗi phân tích AI", "error");
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleAnalyzeMatch = async () => {
    setAnalyzingMatch(true);
    try {
      const token = localStorage.getItem('skillmatch_token');
      const res = await fetch(`${API_URL}/api/candidates/${id}/analyze-match`, {
        method: 'POST', headers: { 'x-auth-token': token }
      });
      const data = await res.json();
      setMatchAnalysis(data);
      setCandidate(prev => ({ ...prev, matchScore: data.matchScore }));
      showToast("Phân tích thành công!", "success");
    } catch (e) { showToast("Lỗi phân tích", "error"); }
    finally { setAnalyzingMatch(false); }
  };

  // --- MODAL & ACTION HANDLERS ---
  const openConfirmModal = (type) => {
    if (type === 'Từ chối') {
      setRejectEmail({ subject: '', content: '' });
    }
    if (type === 'Phỏng vấn') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      const offset = tomorrow.getTimezoneOffset() * 60000;
      const localISOTime = (new Date(tomorrow - offset)).toISOString().slice(0, 16);
      setInterviewForm({ datetime: localISOTime, type: 'Online', location: '', note: '' });
      setConfirmModal({ isOpen: true, type: 'Phỏng vấn', title: 'Gửi lời mời phỏng vấn', message: '' });
    } else {
      setConfirmModal({ isOpen: true, type: type, title: `Xác nhận ${type}`, message: `Bạn có chắc chắn muốn chuyển trạng thái hồ sơ này sang "${type}" không?` });
    }
  };

  const confirmAction = async () => {
    const newStatus = confirmModal.type;
    let interviewPayload = null;

    // Payload gửi xuống Server
    const payload = { status: newStatus };

    if (newStatus === 'Phỏng vấn') {
      if (!interviewForm.datetime || !interviewForm.location) {
        showToast("Vui lòng chọn thời gian và địa điểm/link!", "error");
        return;
      }
      interviewPayload = {
        time: new Date(interviewForm.datetime),
        type: interviewForm.type,
        location: interviewForm.location,
        note: interviewForm.note
      };
      payload.interviewData = interviewPayload;
    }

    // ✅ NẾU TỪ CHỐI VÀ CÓ EMAIL -> GỬI KÈM EMAIL XUỐNG SERVER
    if (newStatus === 'Từ chối' && rejectEmail.content) {
      payload.rejectEmail = rejectEmail;
    }

    setUpdating(true);
    setConfirmModal({ ...confirmModal, isOpen: false });
    try {
      const token = localStorage.getItem('skillmatch_token');
      const response = await fetch(`${API_URL}/api/candidates/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setCandidate(prev => ({ ...prev, status: newStatus }));
        showToast(`Đã cập nhật trạng thái: ${newStatus}`, 'success');
      } else {
        const errData = await response.json();
        showToast(errData.message || "Có lỗi xảy ra", 'error');
      }
    } catch (error) {
      console.error("Lỗi update:", error);
      showToast("Lỗi kết nối server", 'error');
    } finally {
      setUpdating(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '---';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m} phút ${s} giây`;
  };

  const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleString('vi-VN') : '---';

  if (loading) return <div className="flex h-96 items-center justify-center text-gray-500">Đang tải hồ sơ...</div>;

  // Nếu bị chặn bởi Modal Upgrade, hiển thị modal và nền
  if (upgradeModal.show) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UpgradeModal
          isOpen={upgradeModal.show}
          onClose={() => navigate('/business/candidates')} // Đóng modal -> Về trang danh sách
          message={upgradeModal.message}
        />
      </div>
    );
  }

  if (!candidate) return <div className="flex h-96 items-center justify-center text-red-500">Không tìm thấy dữ liệu</div>;

  return (
    <div className="font-sans text-[#111827] bg-[#F3F4F6] min-h-full">
      <UpgradeModal
        isOpen={upgradeModal.show}
        onClose={() => navigate('/business/candidates')}
        message={upgradeModal.message}
      />
      <div className="flex flex-col gap-6">
        {/* INFO CARD */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 shadow-sm">
          <div className="flex gap-5 items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">{candidate.name}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[16px]">mail</span> {candidate.email}</span>
                <span className="flex items-center gap-1.5"><span className="material-symbols-outlined text-[16px]">work</span> {candidate.position}</span>
                <button className="flex items-center gap-1.5 text-indigo-600 " onClick={() => navigate(`/business/candidates/${candidate._id}/cv`)}>
                  <span className="material-symbols-outlined text-[20px] ">visibility</span> Xem CV
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <button onClick={() => openConfirmModal('Đạt')} disabled={updating || candidate.status === 'Đạt'} className={`px-5 py-2 rounded-lg border font-medium transition-colors text-sm shadow-sm ${candidate.status === 'Đạt' ? 'bg-green-100 text-green-700 border-green-200 cursor-not-allowed' : 'bg-white border-green-600 text-green-700 hover:bg-green-50'}`}>
              {candidate.status === 'Đạt' ? 'Đã duyệt đạt' : 'Duyệt: Đạt'}
            </button>
            <button onClick={() => openConfirmModal('Từ chối')} disabled={updating || candidate.status === 'Từ chối'} className={`px-5 py-2 rounded-lg border font-medium transition-colors text-sm shadow-sm ${candidate.status === 'Từ chối' ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'}`}>
              {updating && candidate.status === 'Từ chối' ? 'Đang xử lý...' : 'Từ chối'}
            </button>
            <button onClick={() => openConfirmModal('Phỏng vấn')} disabled={updating || candidate.status === 'Phỏng vấn'} className={`px-5 py-2 rounded-lg text-white font-medium shadow-sm flex items-center justify-center gap-2 transition-colors text-sm ${candidate.status === 'Phỏng vấn' ? 'bg-indigo-700 cursor-default' : 'bg-[#111827] hover:bg-gray-800'}`}>
              {candidate.status === 'Phỏng vấn' ? <><span className="material-symbols-outlined text-[18px]">check</span> Đã mời PV</> : <><span className="material-symbols-outlined text-[18px]">calendar_add_on</span> Mời phỏng vấn</>}
            </button>
          </div>
          {candidate.jobId && (
            <div className="flex flex-col items-center justify-center p-4 bg-blue-50 border border-blue-100 rounded-lg min-w-[150px]">
              <span className="text-xs font-bold text-blue-500 uppercase">Độ phù hợp Job</span>
              <div className="text-3xl font-bold text-blue-700 my-1">
                {matchAnalysis?.matchScore || candidate.matchScore || '--'}%
              </div>
              {!matchAnalysis && !candidate.matchScore ? (
                <button onClick={handleAnalyzeMatch} disabled={analyzingMatch} className="text-xs bg-white border border-blue-200 px-2 py-1 rounded hover:bg-blue-100 text-blue-600 transition-colors">
                  {analyzingMatch ? 'Đang chạy...' : '⚡️ Phân tích ngay'}
                </button>
              ) : (
                <button onClick={() => setActiveTab('evaluation')} className="text-xs text-blue-600 hover:underline">Xem chi tiết</button>
              )}
            </div>
          )}
        </div>

        {/* STATS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4 bg-white border border-[#E5E7EB] rounded-xl p-6 flex flex-col justify-between min-h-[180px] shadow-sm">
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Điểm tổng kết</p>
              <div className="flex items-baseline gap-2 mt-2">
                <span className={`text-5xl font-bold tracking-tighter ${candidate.score >= 50 ? 'text-green-600' : 'text-red-500'}`}>{candidate.score}</span>
                <span className="text-xl font-medium text-gray-400">/100</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600"><span className="truncate font-medium">{candidate.testName}</span></div>
          </div>
          <div className="md:col-span-8 bg-white border border-[#E5E7EB] rounded-xl p-6 flex flex-col justify-center shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
              <div className="px-4 first:pl-0">
                <p className="text-xs font-medium text-gray-500 mb-1">Thời gian bắt đầu</p><p className="text-sm font-semibold text-gray-900">{formatDate(candidate.startedAt)}</p>
                <p className="text-xs font-medium text-gray-500 mb-1">Thời gian nộp bài</p><p className="text-sm font-semibold text-gray-900">{formatDate(candidate.submittedAt || candidate.appliedDate)}</p>
              </div>
              <div className="px-4 pt-4 sm:pt-0"><p className="text-xs font-medium text-gray-500 mb-1">Trạng thái</p><p className="text-base font-semibold text-indigo-600">{candidate.status}</p></div>
              <div className="px-4 pt-4 sm:pt-0"><p className="text-xs font-medium text-gray-500 mb-1">Thời lượng</p><p className="text-lg font-bold text-gray-900">{formatDuration(candidate.duration)}</p></div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="flex flex-col gap-4">
          <div className="border-b border-gray-200">
            <nav className="flex gap-6">
              <button onClick={() => setActiveTab('result')} className={`border-b-2 py-3 px-1 text-sm font-semibold transition-colors ${activeTab === 'result' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Kết quả bài test</button>
              <button onClick={() => setActiveTab('proctoring')} className={`border-b-2 py-3 px-1 text-sm font-semibold flex items-center gap-2 transition-colors ${activeTab === 'proctoring' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <span className="material-symbols-outlined text-[18px]">verified_user</span> Giám sát & Bằng chứng
              </button>
              <button onClick={() => setActiveTab('evaluation')} className={`border-b-2 py-3 px-1 text-sm font-semibold flex items-center gap-2 transition-colors ${activeTab === 'evaluation' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                <span className="material-symbols-outlined text-[18px]">auto_awesome</span> AI Analysis
              </button>
            </nav>
          </div>

          {activeTab === 'evaluation' && (
            <div className="animate-fadeIn space-y-8">
              {matchAnalysis && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2"><span className="material-symbols-outlined">dataset</span> Mức độ phù hợp với công việc</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-blue-900 mb-1">Đánh giá chung:</p>
                      <p className="text-sm text-blue-800 italic bg-white/50 p-3 rounded-lg border border-blue-100">{matchAnalysis.reason}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-blue-900 mb-2">Từ khóa khớp:</p>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const kws = matchAnalysis.keywords_matched || matchAnalysis.keywords || [];
                          if (!kws.length) return <span className="text-sm text-gray-400 italic">Không có từ khóa nổi bật.</span>;
                          return kws.map((kw, i) => (
                            <span key={i} className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-bold rounded-md border border-green-200 flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">check</span> {kw}</span>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isGeneratingAI && (
                <div className="py-20 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-900 font-bold animate-pulse text-lg">AI đang phân tích bài làm...</p>
                </div>
              )}
              {!isGeneratingAI && !aiEvaluation && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                  <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4"><span className="material-symbols-outlined text-3xl text-purple-600">psychology</span></div>
                  <h3 className="text-lg font-bold text-gray-900">Phân tích năng lực chuyên sâu</h3>
                  <button onClick={handleGenerateAI} className="mt-6 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-lg shadow-lg flex items-center gap-2 mx-auto"><span className="material-symbols-outlined">auto_awesome</span> Bắt đầu phân tích ngay</button>
                </div>
              )}
              {!isGeneratingAI && aiEvaluation && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm relative overflow-hidden">
                      <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><span className="material-symbols-outlined text-purple-600">summarize</span> Tổng quan năng lực</h3>
                      <p className="text-gray-700 leading-relaxed text-sm bg-purple-50/50 p-4 rounded-lg border border-purple-100">{aiEvaluation.summary}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white border border-green-200 rounded-xl p-5 shadow-sm bg-green-50/10">
                        <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2"><span className="material-symbols-outlined">thumb_up</span> Điểm mạnh</h4>
                        <ul className="space-y-2">{aiEvaluation.pros?.map((item, idx) => (<li key={idx} className="flex items-start gap-2 text-sm text-gray-700"><span className="material-symbols-outlined text-green-600 text-[18px] mt-0.5">check_circle</span>{item}</li>))}</ul>
                      </div>
                      <div className="bg-white border border-red-200 rounded-xl p-5 shadow-sm bg-red-50/10">
                        <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2"><span className="material-symbols-outlined">thumb_down</span> Điểm cần cải thiện</h4>
                        <ul className="space-y-2">{aiEvaluation.cons?.map((item, idx) => (<li key={idx} className="flex items-start gap-2 text-sm text-gray-700"><span className="material-symbols-outlined text-red-500 text-[18px] mt-0.5">error</span>{item}</li>))}</ul>
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider border-b pb-2">Phân tích kỹ năng</h3>
                      <div className="space-y-4">{aiEvaluation.skills?.map((skill, idx) => (<div key={idx}><div className="flex justify-between mb-1"><span className="text-xs font-bold text-gray-700">{skill.name}</span><span className="text-xs font-bold text-gray-500">{skill.score}/100</span></div><div className="w-full bg-gray-100 rounded-full h-2"><div className={`h-2 rounded-full ${skill.score >= 80 ? 'bg-green-500' : skill.score >= 50 ? 'bg-yellow-400' : 'bg-red-500'}`} style={{ width: `${skill.score}%` }}></div></div></div>))}</div>
                    </div>
                    <div className="bg-[#1F2937] rounded-xl p-5 shadow-lg text-white">
                      <h3 className="font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-wider text-yellow-400"><span className="material-symbols-outlined text-lg">lightbulb</span> Gợi ý phỏng vấn</h3>
                      <ul className="space-y-3">{aiEvaluation.interviewQuestions?.map((q, idx) => (<li key={idx} className="text-xs text-gray-300 leading-relaxed bg-white/10 p-2.5 rounded border border-white/10 italic">"{q}"</li>))}</ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'result' && (
            <div className="space-y-4 animate-fadeIn">
              {testDetails && testDetails.questions ? (
                testDetails.questions.map((q, idx) => {
                  const gradeInfo = candidate.gradedDetail ? candidate.gradedDetail[idx] : null;

                  // ✅ FIX: Xác định loại câu hỏi chính xác (Ưu tiên type câu hỏi -> type bài test)
                  const questionType = q.type || testDetails.testType || 'mcq';
                  const isCodeOrEssay = ['code', 'essay', 'practical'].includes(questionType);

                  // --- NẾU LÀ CODE / TỰ LUẬN ---
                  if (isCodeOrEssay) {
                    return (
                      <div key={idx} className="border border-indigo-100 rounded-lg p-5 bg-white shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="text-base font-semibold text-gray-900 w-3/4">
                            Câu {idx + 1}: {q.question || q.title}
                          </h3>
                          <span className={`text-xs font-bold px-3 py-1 rounded border ${gradeInfo && gradeInfo.score >= 5 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                            {gradeInfo ? `AI Chấm: ${gradeInfo.score}/10` : 'Chưa chấm'}
                          </span>
                        </div>

                        {/* Bài làm */}
                        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4 overflow-x-auto">
                          <p className="text-xs text-gray-500 font-bold uppercase mb-1">Bài làm ứng viên:</p>
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                            {gradeInfo?.answer || "(Không trả lời)"}
                          </pre>
                        </div>

                        {/* Nhận xét AI */}
                        {gradeInfo && gradeInfo.feedback && (
                          <div className="bg-blue-50 p-3 rounded-md border border-blue-100 flex gap-3 items-start">
                            <span className="material-symbols-outlined text-blue-600 text-lg mt-0.5">smart_toy</span>
                            <div>
                              <p className="text-xs font-bold text-blue-800 uppercase mb-1">Nhận xét của AI:</p>
                              <p className="text-sm text-blue-800 italic">"{gradeInfo.feedback}"</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // --- NẾU LÀ TRẮC NGHIỆM (MCQ) ---
                  let userAnswerIdx = candidate.answers ? (candidate.answers[idx.toString()] ?? candidate.answers[idx]) : undefined;
                  const isCorrect = userAnswerIdx != undefined && userAnswerIdx == q.correctAnswer;

                  return (
                    <div key={idx} className={`border-l-4 rounded-lg p-5 bg-white shadow-sm border border-[#E5E7EB] ${isCorrect ? 'border-l-green-500' : 'border-l-red-500'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-base font-semibold text-gray-900 pr-8">Câu {idx + 1}: {q.question || q.title}</h3>
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${isCorrect ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/10'}`}>
                          {isCorrect ? 'Đúng' : 'Sai'}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-2">
                        <div className={`p-3 rounded border ${isCorrect ? 'bg-gray-50 border-gray-200' : 'bg-red-50/50 border-red-100'}`}>
                          <p className={`text-xs mb-1 ${isCorrect ? 'text-gray-500' : 'text-red-500'}`}>Câu trả lời của ứng viên</p>
                          <div className={`flex items-center gap-2 font-medium ${isCorrect ? 'text-gray-900' : 'text-red-700'}`}>
                            <span className="material-symbols-outlined text-[18px]">{isCorrect ? 'check_circle' : 'cancel'}</span>
                            {q.options && userAnswerIdx !== undefined ? q.options[userAnswerIdx] : '(Không trả lời)'}
                          </div>
                        </div>
                        {!isCorrect && (
                          <div className="bg-green-50/50 p-3 rounded border border-green-100">
                            <p className="text-xs text-green-600 mb-1">Đáp án đúng</p>
                            <div className="flex items-center gap-2 font-medium text-green-800">
                              <span className="material-symbols-outlined text-[18px]">check_circle</span>
                              {q.options ? q.options[q.correctAnswer] : 'N/A'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-10 text-gray-500">Đang tải nội dung bài thi...</div>
              )}
            </div>
          )}

          {activeTab === 'proctoring' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fadeIn">
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Thông tin giám sát</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between border-b pb-2"><span className="text-sm text-gray-600">Số ảnh đã chụp:</span><span className="font-bold">{candidate.snapshots ? candidate.snapshots.length : 0}</span></div>
                    <div className="flex justify-between border-b pb-2"><span className="text-sm text-gray-600">Số lần cảnh báo:</span><span className="font-bold text-red-600">{candidate.violationCount || 0}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Buộc dừng thi:</span><span className={`font-bold ${candidate.isTerminated ? 'text-red-600' : 'text-green-600'}`}>{candidate.isTerminated ? 'CÓ' : 'KHÔNG'}</span></div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm h-full">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Hình ảnh ghi lại</h3>
                  {candidate.snapshots && candidate.snapshots.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2">
                      {candidate.snapshots.map((shot, index) => (
                        <div key={index} className="group relative rounded-lg overflow-hidden border border-gray-200 bg-black">
                          <img src={shot.img} alt={`Evidence ${index}`} className="w-full h-32 object-cover opacity-90 hover:opacity-100 transition-opacity" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"><div className="absolute bottom-2 left-2 text-white"><p className="text-xs font-mono font-bold">{new Date(shot.time).toLocaleTimeString('vi-VN')}</p><p className="text-[10px] bg-blue-600/80 px-1.5 py-0.5 rounded w-fit mt-1 backdrop-blur-sm">{shot.note || 'Auto'}</p></div></div>
                        </div>
                      ))}
                    </div>
                  ) : (<div className="flex flex-col items-center justify-center h-48 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg"><span className="material-symbols-outlined text-44 mb-2">no_photography</span><p className="text-sm">Chưa có dữ liệu hình ảnh.</p></div>)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast.show && createPortal(
        <div className={`fixed bottom-20 left-5 z-[9999] px-4 py-3 rounded-lg shadow-xl text-white flex items-center gap-3 animate-slideUp ${toast.type === 'error' ? 'bg-red-600' : 'bg-gray-800'}`}>
          <span className="material-symbols-outlined">{toast.type === 'error' ? 'error' : 'check_circle'}</span>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>,
        document.body
      )}

      {confirmModal.isOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}></div>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[550px] relative z-10 overflow-hidden animate-fadeIn">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-white">
              <h3 className="text-lg font-bold text-gray-900">{confirmModal.title}</h3>
              <button className="text-gray-400 hover:text-gray-600 transition-colors" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            <div className="p-6">
              {confirmModal.type === 'Phỏng vấn' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thời gian phỏng vấn <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-gray-900 bg-white"
                      style={{ colorScheme: 'light' }}
                      value={interviewForm.datetime}
                      onChange={(e) => setInterviewForm({ ...interviewForm, datetime: e.target.value })}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hình thức</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none text-gray-900 bg-white"
                      value={interviewForm.type}
                      onChange={(e) => setInterviewForm({ ...interviewForm, type: e.target.value })}
                    >
                      <option value="Online">Online (Google Meet/Zoom)</option>
                      <option value="Offline">Offline (Tại văn phòng)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Địa điểm / Link họp <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none text-gray-900 bg-white"
                      placeholder={interviewForm.type === 'Online' ? "VD: meet.google.com/abc-xyz" : "VD: Tầng 5, Tòa nhà Tech..."}
                      value={interviewForm.location}
                      onChange={(e) => setInterviewForm({ ...interviewForm, location: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú thêm</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm outline-none resize-none text-gray-900 bg-white"
                      rows="3"
                      placeholder="Dặn dò ứng viên..."
                      value={interviewForm.note}
                      onChange={(e) => setInterviewForm({ ...interviewForm, note: e.target.value })}
                    ></textarea>
                  </div>
                </div>
              )}

              {confirmModal.type === 'Từ chối' && (
                <div className="flex flex-col gap-4">
                  <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm border border-yellow-200 flex gap-2">
                    <span className="material-symbols-outlined text-lg">tips_and_updates</span>
                    <div>
                      <strong>Mẹo:</strong> Hãy gửi phản hồi chi tiết để giữ hình ảnh chuyên nghiệp.
                      Sử dụng AI để viết thư chân thành hơn.
                    </div>
                  </div>

                  {!rejectEmail.content ? (
                    <div className="text-center py-6">
                      <button
                        onClick={handleGenerateRejectEmail}
                        disabled={isGeneratingEmail}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2 mx-auto"
                      >
                        {isGeneratingEmail ? <span className="material-symbols-outlined animate-spin">sync</span> : <span className="material-symbols-outlined">auto_awesome</span>}
                        {isGeneratingEmail ? 'AI đang viết thư...' : 'Nhờ AI soạn thư từ chối'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3 animate-fadeIn">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tiêu đề email</label>
                        <input
                          value={rejectEmail.subject}
                          onChange={(e) => setRejectEmail({ ...rejectEmail, subject: e.target.value })}
                          className="w-full border border-gray-300 rounded p-2 text-sm font-semibold text-gray-900 bg-white focus:ring-2 focus:ring-purple-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nội dung</label>
                        <textarea
                          value={rejectEmail.content.replace(/<br\s*\/?>/gi, '\n')}
                          onChange={(e) => setRejectEmail({ ...rejectEmail, content: e.target.value })}
                          rows="8"
                          className="w-full border border-gray-300 rounded p-2 text-sm text-gray-900 bg-white leading-relaxed focus:ring-2 focus:ring-purple-500 outline-none resize-y"
                        ></textarea>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {confirmModal.type !== 'Phỏng vấn' && confirmModal.type !== 'Từ chối' && (
                <p className="text-gray-600">{confirmModal.message}</p>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}>Hủy</button>
              <button className={`px-5 py-2.5 rounded-lg text-white font-medium shadow-sm transition-all ${confirmModal.type === 'Từ chối' ? 'bg-red-600 hover:bg-red-700' : (confirmModal.type === 'Đạt' ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700')}`} onClick={confirmAction}>
                {confirmModal.type === 'Phỏng vấn' ? 'Gửi lời mời' : (confirmModal.type === 'Từ chối' ? 'Gửi & Loại' : 'Xác nhận')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out forwards; }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        /* Fix icon calendar */
        input[type="datetime-local"]::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; padding: 4px; }
        input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover { opacity: 1; background-color: #f3f4f6; border-radius: 4px; }
      `}</style>

    </div>
  );
}